import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

import { readReviewerIdentity, writeReviewerIdentity } from "./mockupApi";
import { useMockupEditorChat } from "./useMockupEditorChat";

const isAclymateEmail = (email) => /@aclymate\.com$/i.test(email.trim());

const APPROVAL_DESCRIPTION_BUILDERS = {
  apply_principle_across_hill: (input) =>
    `Apply "${input?.principle}" across ${input?.screens?.length || 0} other screen(s)?`,
  open_github_pr: (input) => `Open a PR: ${input?.title || "design-doc update"}?`,
};

const describeApproval = (toolUse) => {
  const builder = APPROVAL_DESCRIPTION_BUILDERS[toolUse?.name];
  return builder ? builder(toolUse.input) : "Approve this action?";
};

const MessageBubble = ({ message }) => {
  const { palette } = useTheme();
  const isReviewer = message.role === "reviewer";
  return (
    <Box
      sx={{
        alignSelf: isReviewer ? "flex-end" : "flex-start",
        bgcolor: isReviewer ? palette.primary.main : palette.backgroundGray.main,
        borderRadius: 2,
        px: 1.5,
        py: 1,
        maxWidth: "85%",
      }}
    >
      <Typography variant="body2" color={isReviewer ? "inherit" : "textPrimary"} sx={isReviewer ? { color: palette.common.white } : {}}>
        {message.text}
      </Typography>
    </Box>
  );
};

const TypingIndicator = ({ label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: "flex-start", py: 0.5 }}>
    <CircularProgress size={14} />
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Stack>
);

const PendingApprovalCard = ({ pending, onRespond }) => {
  const toolUse = pending.toolUses?.[0];
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">Approval needed</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        {describeApproval(toolUse)}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" onClick={() => onRespond(true)}>
          Approve
        </Button>
        <Button size="small" variant="outlined" onClick={() => onRespond(false)}>
          Reject
        </Button>
      </Stack>
    </Paper>
  );
};

const ReviewerIdentityFields = ({ name, email, onNameChange, onEmailChange }) => (
  <Stack spacing={1} sx={{ p: 1.5 }}>
    <Typography variant="body2" color="textSecondary">
      Tell us who you are before chatting.
    </Typography>
    <TextField label="Your name" size="small" value={name} onChange={(event) => onNameChange(event.target.value)} />
    <TextField
      label="Your @aclymate.com email"
      size="small"
      value={email}
      onChange={(event) => onEmailChange(event.target.value)}
      error={Boolean(email) && !isAclymateEmail(email)}
    />
  </Stack>
);

const LiveEditChatPanel = ({
  hillId,
  hillKey,
  knowledgeDocId,
  scenarioId,
  scenarioName,
  screenRoute,
  screenTitle,
  onLiveOverridesChange,
}) => {
  const [open, setOpen] = useState(false);
  const initialIdentity = readReviewerIdentity();
  const [name, setName] = useState(initialIdentity.name);
  const [email, setEmail] = useState(initialIdentity.email);
  const [draft, setDraft] = useState("");

  const { messages, sending, toolStatus, error, pendingApproval, liveOverrides, sendMessage, respondToApproval } =
    useMockupEditorChat({ hillId, hillKey, knowledgeDocId, scenarioId, scenarioName, screenRoute, screenTitle });

  useEffect(() => {
    onLiveOverridesChange(liveOverrides);
  }, [liveOverrides, onLiveOverridesChange]);

  const identityReady = Boolean(name.trim()) && isAclymateEmail(email);
  const lastMessage = messages[messages.length - 1];
  const isStreamingText = Boolean(lastMessage && lastMessage.role === "assistant" && lastMessage.streaming);
  const showTypingIndicator = sending && (Boolean(toolStatus) || !isStreamingText);

  const handleSend = () => {
    const isSendable = Boolean(draft.trim()) && identityReady && !sending;
    if (!isSendable) return;
    writeReviewerIdentity({ name: name.trim(), email: email.trim() });
    sendMessage(draft);
    setDraft("");
  };

  const handleDraftKeyDown = (event) => {
    const shouldSend = event.key === "Enter" && !event.shiftKey;
    if (!shouldSend) return;
    event.preventDefault();
    handleSend();
  };

  if (!open) {
    return (
      <Tooltip title="Chat with the mockup editor">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: (theme) => theme.zIndex.tooltip + 1 }}
        >
          <ChatIcon sx={{ color: (theme) => theme.palette.common.white }} />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper
      elevation={4}
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 380,
        height: 520,
        display: "flex",
        flexDirection: "column",
        zIndex: (theme) => theme.zIndex.tooltip + 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 1.5, bgcolor: "primary.main" }}
      >
        <Typography variant="subtitle1" sx={{ color: "common.white" }}>
          Mockup Editor
        </Typography>
        <IconButton onClick={() => setOpen(false)} sx={{ color: "common.white" }}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      {!identityReady && (
        <ReviewerIdentityFields name={name} email={email} onNameChange={setName} onEmailChange={setEmail} />
      )}
      {identityReady && (
        <Stack spacing={1} sx={{ flexGrow: 1, overflowY: "auto", p: 1.5 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Describe a change — "make the padding smaller", "use a card instead of a list" — and watch it update.
            </Typography>
          )}
          {messages.map((message, index) => (
            <MessageBubble key={`${message.role}-${index}`} message={message} />
          ))}
          {showTypingIndicator && <TypingIndicator label={toolStatus || "Thinking…"} />}
          {pendingApproval && (
            <PendingApprovalCard pending={pendingApproval} onRespond={respondToApproval} />
          )}
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mx: 1.5 }}>
          {error}
        </Alert>
      )}
      {identityReady && (
        <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Describe a change..."
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleDraftKeyDown}
            disabled={sending}
          />
          <IconButton color="primary" onClick={handleSend} disabled={sending || !draft.trim()}>
            <SendIcon />
          </IconButton>
        </Stack>
      )}
    </Paper>
  );
};

export default LiveEditChatPanel;
